"use client";

import { useMemo, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Search, Trash2, NotebookPen, X, Pin, ArrowLeft, Tags } from "lucide-react";
import type { Tag } from "@/db/schema";
import type { NoteWithTags } from "@/lib/queries";
import { emptyTrash } from "@/app/actions";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/theme-toggle";
import { Composer } from "@/components/notes/composer";
import { NoteCard } from "@/components/notes/note-card";
import { NoteEditor } from "@/components/notes/note-editor";

type View = "notes" | "trash";

export function NotesApp({
  notes,
  trash,
  tags,
  dbError,
}: {
  notes: NoteWithTags[];
  trash: NoteWithTags[];
  tags: Tag[];
  dbError: boolean;
}) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [view, setView] = useState<View>("notes");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notes.filter((n) => {
      const matchesQuery =
        !q ||
        (n.title?.toLowerCase().includes(q) ?? false) ||
        n.content.toLowerCase().includes(q);
      const matchesTag = !activeTag || n.tags.some((t) => t.id === activeTag);
      return matchesQuery && matchesTag;
    });
  }, [notes, query, activeTag]);

  const pinned = filtered.filter((n) => n.pinned);
  const others = filtered.filter((n) => !n.pinned);
  const editingNote = editingId ? notes.find((n) => n.id === editingId) ?? null : null;

  function clearTrash() {
    startTransition(async () => {
      const res = await emptyTrash();
      if (res.ok) {
        router.refresh();
        toast.success("Papelera vaciada");
      } else toast.error(res.error);
    });
  }

  return (
    <div className="min-h-screen">
      {/* barra superior */}
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/65">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
          <div className="flex shrink-0 items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
              <NotebookPen className="size-4.5" />
            </span>
            <span className="hidden font-display text-xl font-semibold tracking-tight sm:inline">
              Notas
            </span>
          </div>

          <div className="relative mx-auto w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en tus notas…"
              aria-label="Buscar notas"
              disabled={view === "trash"}
              className="h-10 rounded-full border-transparent bg-muted/70 pl-9 pr-9 focus-visible:bg-background"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Limpiar búsqueda"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-foreground/10"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant={view === "trash" ? "secondary" : "ghost"}
              size="icon"
              aria-label="Papelera"
              title="Papelera"
              onClick={() => setView((v) => (v === "trash" ? "notes" : "trash"))}
            >
              <Trash2 />
            </Button>
            <ThemeToggle />
          </div>
        </div>

        {/* filtro por etiquetas */}
        {view === "notes" && tags.length > 0 && (
          <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 pb-3 sm:px-6">
            <Tags className="size-3.5 shrink-0 text-muted-foreground" />
            <TagChip label="Todas" active={activeTag === null} onClick={() => setActiveTag(null)} />
            {tags.map((t) => (
              <TagChip
                key={t.id}
                label={t.name}
                active={activeTag === t.id}
                onClick={() => setActiveTag((cur) => (cur === t.id ? null : t.id))}
              />
            ))}
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        {view === "notes" ? (
          <>
            <Composer />

            <div className="mt-10">
              {dbError ? (
                <StatusBlock
                  title="Conectando con la base de datos…"
                  body="Esto puede tardar unos segundos al arrancar. Se reintenta automáticamente."
                />
              ) : notes.length === 0 ? (
                <EmptyNotes />
              ) : filtered.length === 0 ? (
                <StatusBlock
                  title="Sin resultados"
                  body={
                    activeTag
                      ? "Ninguna nota coincide con la etiqueta y la búsqueda."
                      : `No encontramos notas para «${query}».`
                  }
                />
              ) : (
                <div className="space-y-8">
                  {pinned.length > 0 && (
                    <section>
                      <SectionLabel icon={<Pin className="size-3 fill-current" />} text="Fijadas" />
                      <div className="notes-masonry mt-3">
                        {pinned.map((n) => (
                          <NoteCard key={n.id} note={n} onOpen={(note) => setEditingId(note.id)} />
                        ))}
                      </div>
                    </section>
                  )}
                  {others.length > 0 && (
                    <section>
                      {pinned.length > 0 && <SectionLabel text="Otras" />}
                      <div className={cn("notes-masonry", pinned.length > 0 && "mt-3")}>
                        {others.map((n) => (
                          <NoteCard key={n.id} note={n} onOpen={(note) => setEditingId(note.id)} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <section>
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon-sm" aria-label="Volver" onClick={() => setView("notes")}>
                  <ArrowLeft />
                </Button>
                <div>
                  <h2 className="font-display text-xl font-semibold">Papelera</h2>
                  <p className="text-sm text-muted-foreground">Las notas eliminadas se pueden restaurar.</p>
                </div>
              </div>
              {trash.length > 0 && (
                <Button variant="destructive" size="sm" onClick={clearTrash} disabled={pending}>
                  <Trash2 /> Vaciar papelera
                </Button>
              )}
            </div>
            {trash.length === 0 ? (
              <StatusBlock title="La papelera está vacía" body="Aquí aparecerán las notas que elimines." />
            ) : (
              <div className="notes-masonry">
                {trash.map((n) => (
                  <NoteCard key={n.id} note={n} variant="trash" />
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <NoteEditor note={editingNote} onClose={() => setEditingId(null)} />
    </div>
  );
}

function TagChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors",
        active
          ? "bg-primary text-primary-foreground ring-transparent"
          : "bg-transparent text-muted-foreground ring-border hover:bg-muted hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function SectionLabel({ icon, text }: { icon?: React.ReactNode; text: string }) {
  return (
    <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {icon}
      {text}
    </h2>
  );
}

function StatusBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border p-10 text-center">
      <h3 className="font-display text-lg font-medium">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function EmptyNotes() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-dashed border-border px-8 py-16 text-center">
      <span className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
        <NotebookPen className="size-7" />
      </span>
      <h3 className="font-display text-2xl font-medium">Tu primera nota te espera</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Captura una idea, una lista o un recordatorio. Usa el cuadro de arriba para empezar a escribir.
      </p>
    </div>
  );
}
